import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "338",
  "slug": "how-to-teach-geography-at-home-homeschool",
  "title": "How to Teach Geography at Home: A Homeschool Parent's Guide",
  "excerpt": "You don't need a textbook or a classroom to raise a kid who knows the world. A globe, a bit of curiosity, and a few good games go a remarkably long way.",
  "date": "2026-06-03",
  "category": "Education",
  "image": "https://images.unsplash.com/photo-1763672080438-8e16c9f91cdd?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxjaGlsZCUyMGdsb2JlfGVufDF8MHx8fDE3ODA0NzczNjJ8MA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "5 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Geography is one of the easiest subjects to teach at home and one of the easiest to teach badly. Done as rote memorisation — capitals, exports, a blank map to fill in — it's forgettable. Done as a way of making sense of the whole world, it becomes the subject that quietly connects history, science, current events, and the food on your table. Here's how to build the second kind."
    },
    {
      "type": "heading",
      "text": "Start With the World, Then Zoom In"
    },
    {
      "type": "paragraph",
      "text": "Put a globe or a big world map somewhere your kids see it every day. The single most useful habit in home geography is placing things in context: when a country comes up in a book, a film, or the news, walk over and find it. Over months, that constant zooming in and out builds a mental map that no worksheet can match. Geography becomes a reflex, not a subject."
    },
    {
      "type": "heading",
      "text": "The Five Themes (A Simple Framework)"
    },
    {
      "type": "paragraph",
      "text": "If you want a backbone for your teaching without buying a curriculum, the classic Five Themes of Geography give you one. They turn 'learn about a place' into five concrete questions:"
    },
    {
      "type": "list",
      "items": [
        "Location — where is it, both absolutely (coordinates) and relatively (near what)?",
        "Place — what is it like there, in nature and in human terms?",
        "Human-environment interaction — how do people and the land shape each other?",
        "Movement — how do people, goods, and ideas travel to and from it?",
        "Region — what does it have in common with the places around it?"
      ]
    },
    {
      "type": "heading",
      "text": "Add a practical task"
    },
    {
      "type": "paragraph",
      "text": "Use a practical task to make their reasoning visible. A handful of projects will teach more than a term of copying maps:"
    },
    {
      "type": "list",
      "items": [
        "Build a salt-dough or clay relief map to feel how mountains and valleys work.",
        "Track a package, a ship, or a migrating bird across the world and mark its route.",
        "Cook a meal from a different country and find where each ingredient grows.",
        "Keep a weather log and compare it with a city in another climate zone.",
        "Run a satellite-image scavenger hunt: can they find a desert, a river delta, a city grid from above?"
      ]
    },
    {
      "type": "heading",
      "text": "Games That Teach Without Feeling Like School"
    },
    {
      "type": "paragraph",
      "text": "Games are where home geography really shines, because there's no class to keep on pace — you can follow a kid's curiosity for as long as it lasts. Map puzzles, country-shape quizzes, capital-matching games, and location-guessing games all turn practice into play. The trick is to let the game raise questions ('why is that whole area empty?') and then chase the answers together."
    },
    {
      "type": "heading",
      "text": "Make the News a Geography Lesson"
    },
    {
      "type": "paragraph",
      "text": "One of the easiest daily habits is also one of the richest: whenever a place comes up — in a news story, a sports result, a book, a film — stop and find it together. Why is there conflict there? Why did the earthquake hit that coast and not this one? Why does that country export what it exports? Geography taught this way never feels like a separate subject; it becomes the lens you use to understand everything else, which is exactly what it's for."
    },
    {
      "type": "heading",
      "text": "Don't Skip Your Own Backyard"
    },
    {
      "type": "paragraph",
      "text": "It's tempting to chase the dramatic and far away, but local geography is where abstract ideas become real. Walk the nearest river and follow where it goes. Work out why your town is where it is — a crossing, a harbour, a mine, a road. Map the route to the shops. A child who understands the landscape they live in has a sturdy hook to hang the rest of the world on, because every new place becomes a comparison to somewhere they know in their bones."
    },
    {
      "type": "heading",
      "text": "By Age"
    },
    {
      "type": "paragraph",
      "text": "Roughly: with young children, focus on continents, oceans, and your own country — big shapes and big stories. In the middle years, layer in countries, capitals, landforms, and the Five Themes. With teens, push into the why — climate, economics, borders, migration — and let them argue about it. The same globe grows with them."
    },
    {
      "type": "heading",
      "text": "Build a Year Without a Textbook"
    },
    {
      "type": "paragraph",
      "text": "If you want gentle structure, give the year a rhythm rather than a syllabus: a country a week, or a continent a month. Keep a simple passport-style notebook where each child records the places they've explored — a flag, a sketch of the outline, one surprising fact, a food they tried. By the end of the year they've assembled their own atlas, built from things they actually did rather than chapters they were made to read. The notebook becomes a record they're proud of and a reference they'll keep coming back to."
    },
    {
      "type": "heading",
      "text": "Let Them Teach You"
    },
    {
      "type": "paragraph",
      "text": "The fastest way to lock in learning is to teach it. Every so often, have a child pick a country and present it to the family — where it is, what it's like, why it matters. The act of preparing to explain forces them to organise what they know and notice the gaps, and an audience of parents and siblings asking questions does more for genuine understanding than any quiz. You'll be surprised how much sticks when a kid knows they have to teach it."
    },
    {
      "type": "paragraph",
      "text": "Most of all, model curiosity. A parent who genuinely wonders where a place is and goes to look will teach more geography than any textbook. If you want one game to anchor that habit, EarthGuessr drops players into real places around the world and turns 'where on Earth is this?' into something the whole family will fight over at the dinner table."
    }
  ]
};
